import { Activity, Flame, Moon, Zap } from "lucide-react";

/**
 * Stylized iPhone mockup with a placeholder "No Fatigue Score" dashboard
 * inside. Real screenshots can be dropped in later via a single <img>.
 */
export default function PhoneMockup({ className = "" }) {
  return (
    <div
      className={`relative mx-auto ${className}`}
      style={{ width: 300, height: 612 }}
      data-testid="phone-mockup"
    >
      {/* Outer glow */}
      <div
        className="absolute -inset-10 rounded-[80px] opacity-60 blur-3xl"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 40%, rgba(0,122,255,0.45) 0%, rgba(0,122,255,0) 70%)",
        }}
      />

      {/* Phone bezel */}
      <div className="absolute inset-0 rounded-[52px] bg-gradient-to-b from-[#1a1a1a] via-[#0a0a0a] to-[#1a1a1a] border border-white/10 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)]">
        <div className="absolute inset-[6px] rounded-[46px] bg-black overflow-hidden">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-28 h-6 rounded-full bg-black z-10" />

          {/* Status bar */}
          <div className="absolute top-2.5 left-6 right-6 flex justify-between text-[10px] text-white/70 z-20">
            <span>9:41</span>
            <span>•••</span>
          </div>

          {/* App content */}
          <div className="absolute inset-0 pt-10 pb-6 px-5 flex flex-col gap-4 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a] to-[#101010]">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Today</p>
                <h3 className="nfp-heading text-2xl text-white mt-0.5">Sprint Day</h3>
              </div>
              <div className="w-9 h-9 rounded-full bg-white/10 border border-white/10 flex items-center justify-center">
                <span className="text-xs nfp-mono text-white/70">JD</span>
              </div>
            </div>

            {/* No Fatigue Score */}
            <div className="rounded-2xl border border-white/10 p-4 bg-[#0f0f0f] relative overflow-hidden">
              <div
                className="absolute -right-10 -top-10 w-32 h-32 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(34,197,94,0.35) 0%, transparent 70%)",
                }}
              />
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">No Fatigue Score</p>
              <div className="flex items-end justify-between mt-1">
                <span className="nfp-heading text-5xl text-white leading-none">86</span>
                <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                  TRAIN AS PLANNED
                </span>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: "86%",
                    background: "linear-gradient(90deg,#22c55e,#00e5ff)",
                  }}
                />
              </div>
            </div>

            {/* Mini metrics */}
            <div className="grid grid-cols-2 gap-2">
              <MiniMetric icon={<Moon className="w-3.5 h-3.5" />} label="Sleep" value="7h 42m" />
              <MiniMetric icon={<Flame className="w-3.5 h-3.5" />} label="Soreness" value="Low" />
              <MiniMetric icon={<Zap className="w-3.5 h-3.5" />} label="Energy" value="High" />
              <MiniMetric icon={<Activity className="w-3.5 h-3.5" />} label="Load" value="Moderate" />
            </div>

            {/* Workout card */}
            <div className="rounded-2xl border border-[#1FA2FF]/40 p-4 bg-gradient-to-br from-[#0a1f3d] to-[#0a0a0a] mt-auto">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#00E5FF]">Track & Field</p>
              <h4 className="nfp-heading text-lg text-white mt-1">Acceleration · 4×40m</h4>
              <p className="text-[11px] text-white/55 mt-1 leading-relaxed">
                Sprint development · 45 min · Block A
              </p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[10px] text-white/50">5 of 7 sessions this week</span>
                <span className="px-3 py-1.5 rounded-full bg-white text-black text-[11px] font-bold">
                  Start
                </span>
              </div>
            </div>
          </div>

          {/* Home indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 rounded-full bg-white/40" />
        </div>
      </div>
    </div>
  );
}

const MiniMetric = ({ icon, label, value }) => (
  <div className="rounded-xl border border-white/8 bg-white/[0.02] px-3 py-2.5">
    <div className="flex items-center gap-1.5 text-white/50">
      {icon}
      <span className="text-[10px] uppercase tracking-[0.15em]">{label}</span>
    </div>
    <p className="nfp-heading text-base text-white mt-1">{value}</p>
  </div>
);
